import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Video, X, ExternalLink, ChevronDown, ChevronRight, Play, Folder } from 'lucide-react';
import { withRouter } from 'react-router-dom';
import { request } from '../../services/utilities';
import { API_URI, tutorialsAPI } from '../../services/constants';
import VideoPlayer from './VideoPlayer';
import './VideoTutorials.css';

// All possible modules — backend filters by role; 'all' prefix videos are injected server-side
const TUTORIAL_MODULES = [
	'paypoint',
	'inventory',
	'pharmacy',
	'lab',
	'radiology',
	'procedure',
	'frontdesk',
	'doctor',
	'nurse',
	'hr',
	'settings',
	'records',
	'hmo',
	'accounting',
	'ivf',
	'therapy',
	'cafeteria',
	'security',
	'audit',
];

const VideoTutorialsPopover = ({ history }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [selectedVideo, setSelectedVideo] = useState(null);
	const [expandedCategories, setExpandedCategories] = useState({});
	const popoverRef = useRef(null);

	// Fetch videos from API
	const fetchVideos = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const modulesParam = TUTORIAL_MODULES.join(',');
			const url = `${tutorialsAPI}/videos?modules=${modulesParam}`;
			const response = await request(url, 'GET', true);
			setCategories(response || []);

			// Auto-expand first category
			if (response && response.length > 0) {
				setExpandedCategories({ [response[0].name]: true });
			}
		} catch (err) {
			console.error('Error fetching videos:', err);
			setError('Failed to load tutorials');
		} finally {
			setLoading(false);
		}
	}, []);

	// Fetch videos when popover opens
	useEffect(() => {
		if (isOpen) {
			fetchVideos();
		}
	}, [isOpen, fetchVideos]);

	// Close popover when clicking outside
	useEffect(() => {
		const handleClickOutside = event => {
			if (popoverRef.current && !popoverRef.current.contains(event.target)) {
				if (!selectedVideo) {
					setIsOpen(false);
				}
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, selectedVideo]);

	// Toggle category expansion
	const toggleCategory = categoryName => {
		setExpandedCategories(prev => ({
			...prev,
			[categoryName]: !prev[categoryName],
		}));
	};

	// Handle video selection
	const handleVideoSelect = video => {
		setSelectedVideo(video);
	};

	// Close video player
	const closeVideoPlayer = () => {
		setSelectedVideo(null);
	};

	// Open full page
	const openFullPage = () => {
		setIsOpen(false);
		history.push('/tutorials');
	};

	// Get total video count
	const totalVideos = categories.reduce((sum, cat) => sum + cat.videos.length, 0);

	return (
		<div className="video-tutorials-container" ref={popoverRef}>
			{/* Trigger Button */}
			<button className="icon-button tutorials-trigger" onClick={() => setIsOpen(!isOpen)} title="Video Tutorials">
				<Video size={20} />
				{totalVideos > 0 && <span className="tutorials-badge">{totalVideos}</span>}
			</button>

			{/* Popover */}
			{isOpen && (
				<div className="tutorials-popover">
					<div className="tutorials-popover-header">
						<h4 className="text-white">
							<Video size={18} /> Video Tutorials
						</h4>
						<div className="header-actions">
							<button className="open-full-btn" onClick={openFullPage} title="Open in full page">
								<ExternalLink size={16} />
							</button>
							<button className="close-btn" onClick={() => setIsOpen(false)}>
								<X size={18} />
							</button>
						</div>
					</div>

					<div className="tutorials-popover-content">
						{loading && (
							<div className="tutorials-loading">
								<div className="loading-spinner"></div>
								<p>Loading tutorials...</p>
							</div>
						)}

						{error && (
							<div className="tutorials-error">
								<p>{error}</p>
								<button onClick={fetchVideos}>Retry</button>
							</div>
						)}

						{!loading && !error && categories.length === 0 && (
							<div className="tutorials-empty">
								<Video size={48} color="#ccc" />
								<p>No tutorials available</p>
							</div>
						)}

						{!loading && !error && categories.length > 0 && (
							<div className="tutorials-list">
								{categories.map(category => (
									<div key={category.name} className="tutorial-category">
										<div className="category-header" onClick={() => toggleCategory(category.name)}>
											<span className="category-icon">
												{expandedCategories[category.name] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
											</span>
											<Folder size={16} color="#1e62eb" />
											<span className="category-name">{category.displayName}</span>
											<span className="category-count">{category.videos.length}</span>
										</div>

										{expandedCategories[category.name] && (
											<div className="category-videos">
												{category.videos.map(video => (
													<div
														key={video.id}
														className={`video-item ${selectedVideo?.id === video.id ? 'active' : ''}`}
														onClick={() => handleVideoSelect(video)}
													>
														<Play size={14} />
														<span className="video-name">{video.displayName}</span>
													</div>
												))}
											</div>
										)}
									</div>
								))}
							</div>
						)}
					</div>

					{/* Mini Video Player */}
					{selectedVideo && (
						<div className="tutorials-player-section">
							<div className="player-header">
								<span>{selectedVideo.displayName}</span>
								<button onClick={closeVideoPlayer}>
									<X size={16} />
								</button>
							</div>
							<VideoPlayer src={`${API_URI}${selectedVideo.path}`} title={selectedVideo.displayName} />
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default withRouter(VideoTutorialsPopover);
